// Single source of truth for "which months can a rep create/submit a Planner
// plan for" and "is today within the allowed day-of-month submission window."
// Replaces what used to be 3 separately hand-rolled month-math implementations
// across Planner.jsx and NavigationHeader.jsx.
import { PERMISSIONS } from "../Permissions";

// How many months ahead a plan can be submitted for, by default (no special
// permission) vs. for an employee granted CAN_SUBMIT_PLANNER_EXTENDED.
// Change these constants to retune either tier app-wide.
export const PLANNER_DEFAULT_MONTHS_AHEAD = 1;
export const PLANNER_MAX_MONTHS_AHEAD = 3;

// The single place that maps "does this employee have the extended-window
// permission" to an actual month count, so every screen stays in sync.
export const getMaxMonthsAhead = (permissions = []) =>
  permissions.includes(PERMISSIONS.CAN_SUBMIT_PLANNER_EXTENDED)
    ? PLANNER_MAX_MONTHS_AHEAD
    : PLANNER_DEFAULT_MONTHS_AHEAD;

// Number of calendar months between `target` and `base` (target - base),
// counted as a running total (year * 12 + month) so it's correct across any
// year boundary, not just a Dec -> Jan rollover.
export const getMonthOffset = (target, base = new Date()) => {
  const targetTotal = target.getFullYear() * 12 + target.getMonth();
  const baseTotal = base.getFullYear() * 12 + base.getMonth();
  return targetTotal - baseTotal;
};

// True for any of the next `maxMonthsAhead` months (not the current month).
// Defaults to the restrictive tier so a caller that forgets to pass the
// permission-derived value fails closed rather than silently over-granting.
export const isSubmittableMonth = (target, base = new Date(), maxMonthsAhead = PLANNER_DEFAULT_MONTHS_AHEAD) => {
  const offset = getMonthOffset(target, base);
  return offset >= 1 && offset <= maxMonthsAhead;
};

// True for the current month or any of the next `maxMonthsAhead` months.
export const isCurrentOrSubmittableMonth = (target, base = new Date(), maxMonthsAhead = PLANNER_DEFAULT_MONTHS_AHEAD) => {
  const offset = getMonthOffset(target, base);
  return offset >= 0 && offset <= maxMonthsAhead;
};

// The unchanged day-of-month (e.g. 15-25) submission window, driven by the
// backend-configured plannerSettings.startDate/endDate.
export const isWithinSubmissionDayWindow = (plannerSettings, base = new Date()) => {
  if (!plannerSettings || plannerSettings === "isLoading") {
    return false;
  }
  const { startDate, endDate } = plannerSettings;
  if (!startDate || !endDate) {
    return false;
  }
  const today = base.getDate();
  return today >= startDate && today <= endDate;
};
